import React from "react";
import { useTranslation } from "react-i18next";
import Button from "components/Button";
import { SnapshotOUT } from "api-client/generated/api";
import SnapshotListItem from "./SnapshotListItem";

interface SnapshotListProps {
  snapshots: SnapshotOUT[];
  loading: boolean;
  hasMore: boolean;
  restoringId: number | null;
  onDelete: (snapshotId: number) => void;
  onRestore: (snapshotId: number) => void;
  onLoadMore: () => void;
}

function SnapshotList({
  snapshots,
  loading,
  hasMore,
  restoringId,
  onDelete,
  onRestore,
  onLoadMore,
}: SnapshotListProps) {
  const { t, i18n } = useTranslation();
  const locale = i18n.resolvedLanguage ?? i18n.language ?? "en";

  const timeFormatter = React.useMemo(
    () =>
      new Intl.DateTimeFormat(locale, {
        hour: "2-digit",
        minute: "2-digit",
      }),
    [locale]
  );
  const dateTimeFormatter = React.useMemo(
    () =>
      new Intl.DateTimeFormat(locale, {
        dateStyle: "short",
        timeStyle: "short",
      }),
    [locale]
  );

  const formatDate = React.useCallback(
    (dateString: string) => {
      const date = new Date(dateString);
      const now = new Date();
      const isToday =
        date.getFullYear() === now.getFullYear() &&
        date.getMonth() === now.getMonth() &&
        date.getDate() === now.getDate();

      if (isToday) {
        return `${t("date.today")} ${timeFormatter.format(date)}`;
      }

      return dateTimeFormatter.format(date);
    },
    [dateTimeFormatter, t, timeFormatter]
  );

  if (snapshots.length === 0 && !loading) {
    return (
      <div className="text-center text-gray-500 py-8">
        {t("mappingEditor.noFullSnapshots")}
      </div>
    );
  }

  return (
    <div className="divide-y divide-gray-200">
      {snapshots.map((snapshot) => (
        <SnapshotListItem
          key={snapshot.id}
          snapshot={snapshot}
          formattedDate={formatDate(snapshot.created_at)}
          restoringId={restoringId}
          onDelete={onDelete}
          onRestore={onRestore}
        />
      ))}
      {hasMore && (
        <div className="flex justify-center pt-4">
          <Button
            type="gray"
            width="fit-content"
            onClick={onLoadMore}
            loading={loading}
            disable={loading}
          >
            {t("mappingEditor.loadMore")}
          </Button>
        </div>
      )}
    </div>
  );
}

export default SnapshotList;
